import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, GuardResult, MaybeAsync, Router } from '@angular/router';
import { LandingPages } from '@shared/constants/landing-page.constants';
import { StartPageVersions } from '@shared/constants/startpage-version.constants';
import { firstTruthy } from '@shared/rxjs-operators';
import { hasProp } from '@shared/utils';
import { map } from 'rxjs';
import { ConfigService } from '../services/config.service';
import { ErrorPageReasons } from '../shared/constants';

export const redirectGuard = (configService: ConfigService, router: Router, navigationTargetUrl: string): MaybeAsync<GuardResult> => {
    return configService.configObs.pipe(
        firstTruthy(),
        map(({ ixservConfig, portal }) => {
            if (navigationTargetUrl !== '/' && !navigationTargetUrl.startsWith('/?code=')) {
                const url = router.parseUrl(navigationTargetUrl);

                if (!hasProp(url.queryParams, 'code')) {
                    return true;
                }

                delete url.queryParams.code;
                return url;
            }

            const landingPage: string = ixservConfig.landingPage ? ixservConfig.landingPage : portal.landingPage;
            const landingPageUrl = router.parseUrl(landingPage);

            // Compare via the parsed path so that a landing page like '/start' from the ixserv config is recognized as well
            const isStartLandingPage = landingPageUrl.root.children['primary']?.toString() === LandingPages.Start;

            if (isStartLandingPage && portal.startPageVersion === StartPageVersions.None) {
                console.error(
                    `[RedirectGuard] Invalid config: effective landing page is "start" but startPageVersion is "none". Redirecting to error page.`,
                );
                return router.createUrlTree(['error'], { queryParams: { reason: ErrorPageReasons.Config, redirect: navigationTargetUrl } });
            }

            return landingPageUrl;
        }),
    );
};

export const redirectCanActivateFn: CanActivateFn = (_, state) => {
    const router = inject(Router);
    const configService = inject(ConfigService);

    const navigationTargetUrl = state.url;

    return redirectGuard(configService, router, navigationTargetUrl);
};

export const redirectCanMatchFn: CanMatchFn = () => {
    const router = inject(Router);
    const configService = inject(ConfigService);

    const navigationTargetUrl = router.getCurrentNavigation()?.extractedUrl.toString() || '/';

    return redirectGuard(configService, router, navigationTargetUrl);
};
